import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "../net/api.js";
import { navigate } from "../router.js";
import { colourFor } from "@obean/beans";
import { Bean } from "../ui/Bean.js";

/** How often a signed-in person's open app looks for invites from friends. */
const POLL_MS = 20_000;

/**
 * In-app invites (ADR 0016): while a signed-in person has the app open, an invite from a friend
 * shows here, "Mia invited you to play", with Join and Not now. Joining makes this browser a member
 * of the room (no link needed) and goes there. Guests never poll.
 */
export function FriendInvites({ currentRoom }: { currentRoom?: string }) {
  const invites = useSignal<{ id: string; roomId: string; fromName: string }[]>([]);
  const busy = useSignal(false);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const { invites: waiting } = await api.friendInvites();
        if (!stopped) invites.value = waiting.filter((i) => i.roomId !== currentRoom);
      } catch {
        // Not signed in (401), or offline: try again later, quietly.
      }
      if (!stopped) timer = setTimeout(() => void poll(), POLL_MS);
    };
    // Only for signed-in people: one look at /api/me first.
    void api.me().then(
      (me) => {
        if (me.signedIn && !stopped) void poll();
      },
      () => undefined,
    );
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [currentRoom]);
  const invite = invites.value[0];
  if (!invite) return null;
  return (
    <aside class="friend-invite" role="status" aria-live="polite">
      <p>
        <strong>{invite.fromName}</strong> invited you to play.
      </p>
      <div class="actions">
        <button
          type="button"
          disabled={busy.value}
          onClick={() => {
            busy.value = true;
            api.acceptInvite(invite.id).then(
              ({ roomId }) => {
                invites.value = [];
                busy.value = false;
                navigate(`/r/${roomId}`);
              },
              () => {
                invites.value = invites.value.slice(1);
                busy.value = false;
              },
            );
          }}
        >
          Join
        </button>
        <button
          type="button"
          class="secondary"
          onClick={() => {
            invites.value = invites.value.slice(1);
            void api.declineInvite(invite.id).catch(() => undefined);
          }}
        >
          Not now
        </button>
      </div>
    </aside>
  );
}

/** "Invite a friend" in a room: signed-in members ask friends in (no link needed). */
export function InviteFriends({ roomId }: { roomId: string }) {
  const friends = useSignal<{ id: string; name: string; bean?: string | null }[]>([]);
  const sent = useSignal<ReadonlySet<string>>(new Set());
  useEffect(() => {
    void api.friends().then(
      (f) => {
        friends.value = f.friends;
      },
      () => undefined, // not signed in, or no friends yet: nothing to show
    );
  }, [roomId]);
  if (friends.value.length === 0) return null;
  return (
    <div class="card invite-friends">
      <h2>Invite a friend</h2>
      <ul>
        {friends.value.map((f) => (
          <li key={f.id}>
            <Bean colour={colourFor(f.id)} extra={f.bean ?? null} size={32} />
            <span>{f.name}</span>
            <button
              type="button"
              class="secondary"
              disabled={sent.value.has(f.id)}
              onClick={() => {
                api.inviteFriend(f.id, roomId).then(
                  () => {
                    sent.value = new Set([...sent.value, f.id]);
                  },
                  () => undefined,
                );
              }}
            >
              {sent.value.has(f.id) ? "Invited" : "Invite"}
            </button>
          </li>
        ))}
      </ul>
      <p class="hint">They see it if Obean is open; otherwise send the link.</p>
    </div>
  );
}
